import React, { useEffect, useMemo, useCallback, useState } from "react";
import axios from "axios";
import { BookOpen, Edit, Headphones, PenLine, Plus, Save, Trash2, X } from "lucide-react";
import { config } from "../../../config";

const apiUrl = config.apiUrl;
const sections = ["listening", "reading", "writing"];

const emptySubject = {
  name: "IELTS",
  description: "",
  order: 0,
  is_active: true,
};

const emptyTest = {
  ielts_subject_id: "",
  section: "listening",
  title: "",
  passage_title: "",
  passage_text: "",
  instruction: "",
  question_title: "",
  question_text: "",
  questions: [],
  audio: null,
  image: null,
  image2: null,
  image3: null,
  logo: null,
  sample_answer: "",
  time_limit: 20,
  order: 0,
  is_published: true,
};

const sectionIcon = {
  listening: Headphones,
  reading: BookOpen,
  writing: PenLine,
};

const newQuestion = (number) => ({
  number,
  prompt: "",
  type: "fill_blank",
  answer: "",
  after_text: "",
  options: [],
  explanation: "",
  metadata: {},
});

const mixedExampleTemplate = `[FLOW]
[BOX]
The visitor arrives at [[1]]
↓
[BOX]
Collects the [[2]] from reception
↓
[BOX]
Meets the guide in the [[3]]
[/FLOW]

Complete the notes below.
Opening time: [[4]]
Ticket price: [[5]]

[TABLE:Area|Main activity|Time]
North Wing|Photography workshop|[[6]]
Garden Hall|Children's story session|[[7]]
[/TABLE]

[GRID:A,B,C,D]
8|Map of the museum
9|Audio guide desk
10|Cafe location
[/GRID]`;

const mixedExampleQuestions = [
  { number: 1, type: "flowchart", prompt: "", answer: "station", after_text: "", options: [], explanation: "Flowchart blank from the arrival step.", metadata: {} },
  { number: 2, type: "flowchart", prompt: "", answer: "badge", after_text: "", options: [], explanation: "The visitor collects a badge from reception.", metadata: {} },
  { number: 3, type: "flowchart", prompt: "", answer: "lobby", after_text: "", options: [], explanation: "The guide is waiting in the lobby.", metadata: {} },
  { number: 4, type: "fill_blank", prompt: "", answer: "9:30", after_text: "", options: [], explanation: "The opening time is 9:30.", metadata: {} },
  { number: 5, type: "fill_blank", prompt: "", answer: "12", after_text: "", options: [], explanation: "The ticket price is 12.", metadata: {} },
  { number: 6, type: "table", prompt: "", answer: "10:15", after_text: "", options: [], explanation: "The photography workshop starts at 10:15.", metadata: {} },
  { number: 7, type: "table", prompt: "", answer: "11:45", after_text: "", options: [], explanation: "The story session starts at 11:45.", metadata: {} },
  { number: 8, type: "grid", prompt: "", answer: "B", after_text: "", options: ["A", "B", "C", "D"], explanation: "The map is shown as option B in the grid.", metadata: {} },
  { number: 9, type: "grid", prompt: "", answer: "D", after_text: "", options: ["A", "B", "C", "D"], explanation: "The audio guide desk is option D.", metadata: {} },
  { number: 10, type: "grid", prompt: "", answer: "A", after_text: "", options: ["A", "B", "C", "D"], explanation: "The cafe location is option A.", metadata: {} },
  {
    number: 11,
    type: "mcq",
    prompt: "What does the speaker recommend booking in advance?",
    answer: "The photography workshop",
    after_text: "",
    options: ["The cafe table", "The photography workshop", "The gift shop tour", "The library pass"],
    explanation: "This standalone MCQ renders after the template without colliding with flowchart answers.",
    metadata: {},
  },
  {
    number: 12,
    type: "mcq",
    prompt: "Why is the Garden Hall popular today?",
    answer: "A children's event is running",
    after_text: "",
    options: ["It has free coffee", "A children's event is running", "It closes early", "A new shop opened"],
    explanation: "The Garden Hall is busy because of the children's session.",
    metadata: {},
  },
  {
    number: 13,
    type: "matching",
    prompt: "Match the North Wing with the correct feature.",
    answer: "Photography",
    after_text: "",
    options: ["Photography", "Food", "Maps", "Tickets"],
    explanation: "North Wing matches Photography.",
    metadata: {},
  },
  {
    number: 14,
    type: "matching",
    prompt: "Match the Garden Hall with the correct feature.",
    answer: "Story session",
    after_text: "",
    options: ["Audio guide", "Story session", "Main exit", "Reception"],
    explanation: "Garden Hall matches the story session.",
    metadata: {},
  },
  {
    number: 15,
    type: "sentence_completion",
    prompt: "Visitors should return their badges before leaving the",
    answer: "building",
    after_text: ".",
    options: [],
    explanation: "Sentence completion uses the same text answer state as fill blanks.",
    metadata: {},
  },
  {
    number: 16,
    type: "fill_blank",
    prompt: "The emergency contact desk is beside the",
    answer: "stairs",
    after_text: ".",
    options: [],
    explanation: "This regular blank proves text inputs work beside MCQs and flowcharts.",
    metadata: {},
  },
];

// --- LISTENING EXAMPLES ---

const listeningPart1Template = `Car Rental Inquiry

Example
Nationality: American
Contact number: [[1]]
Send written quote by: [[2]]
Price for renting: $ [[3]] daily

Special requirements for the room:
• an extra [[4]]
• most important facility: [[5]]

Extra equipment:
• they should have a [[6]]
• as well as a [[7]]
Pick them up from the [[8]]
The caravan driver's age: [[9]]
The registered licence issued in: [[10]]`;

const listeningPart1Questions = Array.from({ length: 10 }, (_, i) => ({
  number: i + 1, type: "fill_blank", prompt: "", answer: "Sample Answer", after_text: "", options: [], explanation: "", metadata: {}
}));

const listeningPart2Template = `Harvesting and Processing Cocoa Beans

[FLOW]
[BOX]
Chocolate beans are [[11]] and then bags are shipped.
↓
[BOX]
Bags are then [[12]] and weighed by machines.
↓
[BOX]
Next chocolate beans are [[13]] in a hopper.
↓
[BOX]
After being roasted at a high temperature
↓
[BOX]
Boiled chocolate beans are [[14]] and cracked.
↓
[BOX]
Roasted beans are [[15]] in the pocket.
↓
[BOX]
Final output is [[16]].
[/FLOW]

Question 17 - 20
What does each type of coffee taste like? Choose the correct group A-D for each item
[GRID:A,B,C,D]
17|First Crack
18|Green Beans
19|French Roast
20|Espresso Smoky
[/GRID]
A | intense
B | mild
C | chocolaty
D | smoky`;

const listeningPart2Questions = [
  ...Array.from({ length: 6 }, (_, i) => ({
    number: i + 11, type: "flowchart", prompt: "", answer: "Sample", after_text: "", options: [], explanation: "", metadata: {}
  })),
  ...Array.from({ length: 4 }, (_, i) => ({
    number: i + 17, type: "grid", prompt: "", answer: ["A", "B", "C", "D"][i % 4], after_text: "", options: ["A", "B", "C", "D"], explanation: "", metadata: {}
  })),
];

const listeningPart3Template = `Question 27 - 30
Which feature is associated with each type of Moa? Choose the correct group A-F for each item.

[GRID:A,B,C,D,E,F]
27|the North Island Giant Moa
28|the Crested Moa
29|the Stout-legged Moa
30|the Eastern Moa
[/GRID]
A | the much taller female
B | less fossils left
C | the biggest eggs
D | feeding at night
E | better vocal sound
F | poor eyesight`;

const listeningPart3Questions = [
  { number: 21, type: "mcq", prompt: "What is the thing that makes the Moa similar to dinosaur?", answer: "Both are of interest to the public.", options: ["Both are of interest to the public.", "Both are extinct at similar time.", "Both left lots of fossil remains."], after_text: "", explanation: "", metadata: {} },
  { number: 22, type: "mcq", prompt: "What is the difference between Moa and other birds?", answer: "no wing bones", options: ["no wing bones", "a long tail", "a smaller head"], after_text: "", explanation: "", metadata: {} },
  { number: 23, type: "mcq", prompt: "What's the special feature of their chicks?", answer: "They can find food by themselves.", options: ["They never return to the nests.", "Most of them die within two months after birth.", "They can find food by themselves."], after_text: "", explanation: "", metadata: {} },
  { number: 24, type: "mcq", prompt: "What is the tutor's opinion on male hatching the eggs?", answer: "Option A", options: ["Option A", "Option B", "Option C"], after_text: "", explanation: "", metadata: {} },
  { number: 25, type: "mcq", prompt: "Question 25", answer: "Option A", options: ["Option A", "Option B", "Option C"], after_text: "", explanation: "", metadata: {} },
  { number: 26, type: "mcq", prompt: "Question 26", answer: "Option A", options: ["Option A", "Option B", "Option C"], after_text: "", explanation: "", metadata: {} },
  { number: 27, type: "grid", prompt: "", answer: "A", options: ["A", "B", "C", "D", "E", "F"], after_text: "", explanation: "", metadata: {} },
  { number: 28, type: "grid", prompt: "", answer: "B", options: ["A", "B", "C", "D", "E", "F"], after_text: "", explanation: "", metadata: {} },
  { number: 29, type: "grid", prompt: "", answer: "C", options: ["A", "B", "C", "D", "E", "F"], after_text: "", explanation: "", metadata: {} },
  { number: 30, type: "grid", prompt: "", answer: "D", options: ["A", "B", "C", "D", "E", "F"], after_text: "", explanation: "", metadata: {} },
];

const listeningPart4Template = `Complete the notes below. Write ONE WORD ONLY for each answer.

[TABLE:Subject|Location|Details]
Geography|[[31]]|Needs to bring a [[32]]
Math|[[33]]|The exam will be on [[34]]
History|[[35]]|Focuses on the [[36]] era
Science|[[37]]|Practical test in the [[38]]
Art|[[39]]|Bring your own [[40]]
[/TABLE]`;

const listeningPart4Questions = Array.from({ length: 10 }, (_, i) => ({
  number: i + 31, type: "table", prompt: "", answer: "Sample", after_text: "", options: [], explanation: "", metadata: {}
}));

// --- READING EXAMPLES ---

const readingPart1Template = `The text has eight sections. Choose the correct heading for the A-F section.

[DRAGDROP]
1|Paragraph A
2|Paragraph B
3|Paragraph C
4|Paragraph D
5|Paragraph E
6|Paragraph F
--
A | Reason why some certain species were not included in it
B | Achievement of Paleobiology Database since
C | Supporter of database recreated its value
D | The purpose of this paleobiology data
E | Criticism on the project which is waste of fund
F | Potential error exists in the database
G | Doubts about the reliability of fossil data
H | The need for a complete record of living species
[/DRAGDROP]

Question 7 - 9
Use the information in the passage to match the people with opinions below. Choose the correct group A-D for each item.
[GRID:A,B,C,D]
7|Creating the Database would help scientist to identify connections of all species
8|Believed in contribution of detailed statistics should cover beyond the known species
9|reached a contradictory finding to the tremendous species die-out
[/GRID]
A | Jonathan Adrain
B | John Alroy
C | David Jablonsky
D | Edward O. Wilson
`;

const readingPart1Questions = [
  ...Array.from({ length: 6 }, (_, i) => ({
    number: i + 1, type: "matching", prompt: "", answer: ["A","B","C","D","E","F"][i], after_text: "", options: [], explanation: "", metadata: {}
  })),
  ...Array.from({ length: 3 }, (_, i) => ({
    number: i + 7, type: "grid", prompt: "", answer: ["A","B","C"][i], after_text: "", options: ["A", "B", "C", "D"], explanation: "", metadata: {}
  })),
  { number: 10, type: "mcq", prompt: "Please choose TWO CORRECT descriptions about the The Paleobiology Database in this passage:", answer: "almost all the experts welcome this project", options: ["almost all the experts welcome this project", "intrigues both positive and negative opinions from various experts", "all different creature in the database have unique name", "aims to embrace all fossil information globally"], after_text: "", explanation: "", metadata: {} },
  { number: 11, type: "mcq", prompt: "Please choose TWO CORRECT descriptions about the The Paleobiology Database in this passage:", answer: "aims to embrace all fossil information globally", options: ["almost all the experts welcome this project", "intrigues both positive and negative opinions from various experts", "all different creature in the database have unique name", "aims to embrace all fossil information globally"], after_text: "", explanation: "", metadata: {} },
  { number: 12, type: "mcq", prompt: "According to the passage, jellyfish belongs to which category of The Paleobiology Database?", answer: "untraceable species", options: ["repetition breed", "untraceable species", "specifically detailed species", "currently living creature"], after_text: "", explanation: "", metadata: {} },
  { number: 13, type: "mcq", prompt: "What is the author's suggestion according to the end of passage?", answer: "Option A", options: ["Option A", "Option B", "Option C", "Option D"], after_text: "", explanation: "", metadata: {} },
];

const readingPart2Template = `Question 21 - 26
Complete the sentences below. Write NO MORE THAN TWO WORDS from the passage for each answer.

The early Polynesians were known as daring [[21]].
They brought animals and [[22]] with them.
The ancient cemetery contained bones of the [[23]] people.
The discoveries opened a window into the [[24]] world.
Scientists found slow-growing [[25]] around the Pacific.
They also analyzed sediments in alpine lakes in [[26]].`;

const readingPart2Questions = [
  ...Array.from({ length: 7 }, (_, i) => ({
    number: i + 14, type: "true_false", prompt: `Captain cook once expected Hawaii might speak another language of people from other pacific islands. (Q${i + 14})`, answer: ["TRUE", "FALSE", "NOT GIVEN"][i % 3], after_text: "", options: [], explanation: "", metadata: {}
  })),
  ...Array.from({ length: 6 }, (_, i) => ({
    number: i + 21, type: "fill_blank", prompt: `Complete the sentence for question ${i + 21}`, answer: `Answer ${i + 21}`, after_text: "", options: [], explanation: "", metadata: {}
  })),
];

const readingPart3Template = `Question 27 - 34
Do the following statements agree with the views of the writer in Reading Passage 3?
YES | NO | NOT GIVEN

[[27]] The grapefruit is generally sweet.
[[28]] Naringin is a chemical that tastes sweet.
[[29]] Some nutritionists believe naringin can prevent cancer.
[[30]] The Linguagen Corporation is based in New Jersey.
[[31]] The test participants drank from three different cups.
[[32]] AMP blocks the bitterness without losing nutrition.
[[33]] Bitterness is useless for plants.
[[34]] Humans are the only species that enjoys bitter taste.

Question 35 - 38
Complete the summary. Write NO MORE THAN TWO WORDS from the text for each answer.

The reason why grapefruit tastes bitter is because a substance called [[35]] contained in it. However, bitterness plays a significant role for plants. It gives a signal that a certain plant is [[36]]. For human beings, different person carries various genetic abilities to taste bitterness. According to a scientist at the University of Utah, [[37]] have exceptionally plenty of [[38]], which allows them to perceive bitter compounds.`;

const readingPart3Questions = [
  ...Array.from({ length: 8 }, (_, i) => ({
    number: i + 27, type: "true_false", prompt: "", answer: ["YES", "NO", "NOT GIVEN"][i % 3], after_text: "", options: [], explanation: "", metadata: {}
  })),
  ...Array.from({ length: 4 }, (_, i) => ({
    number: i + 35, type: "fill_blank", prompt: "", answer: `Answer ${i + 35}`, after_text: "", options: [], explanation: "", metadata: {}
  })),
  { number: 39, type: "mcq", prompt: "What is the main feature of AMP according to this passage?", answer: "offset bitter flavour in food", options: ["offset bitter flavour in food", "only exist in 304 cup", "tastes like citrus", "chemical reaction when meets biscuit"], after_text: "", explanation: "", metadata: {} },
  { number: 40, type: "mcq", prompt: "What is the main function of G protein?", answer: "collecting taste molecule", options: ["collecting taste molecule", "Option B", "Option C", "Option D"], after_text: "", explanation: "", metadata: {} },
];

// Extract all question numbers + types from a template string.
// [[N]]  → fill_blank
// GRID rows (num|label lines inside [GRID:]) → mcq with the grid columns as options
const extractTemplateQuestions = (template) => {
  if (!template) return [];
  const items = new Map();

  // fill_blank: [[N]]
  const blanks = template.match(/\[\[(\d+)\]\]/g) || [];
  blanks.forEach((m) => {
    const n = parseInt(m.replace(/[[\]]/g, ""));
    if (!items.has(n)) items.set(n, { type: "fill_blank", options: [] });
  });

  // mcq: rows inside [GRID:A,B,C,D] ... [/GRID]
  const gridBlocks = template.match(/\[GRID(?::[^\]]*)?\][\s\S]*?\[\/GRID\]/g) || [];
  gridBlocks.forEach((block) => {
    const colsMatch = block.match(/\[GRID:(.*?)\]/);
    const cols = colsMatch ? colsMatch[1].split(",").map((s) => s.trim()) : ["A", "B", "C", "D"];
    block.split("\n").slice(1).forEach((line) => {
      if (line.trim() === "[/GRID]") return;
      const parts = line.split("|");
      const n = parseInt(parts[0]?.trim());
      if (!isNaN(n)) items.set(n, { type: "mcq", options: cols });
    });
  });

  // dragdrop: [DRAGDROP] ... [/DRAGDROP]
  const dragBlocks = template.match(/\[DRAGDROP\][\s\S]*?\[\/DRAGDROP\]/g) || [];
  dragBlocks.forEach((block) => {
    let section = "rows";
    block.split("\n").slice(1).forEach((line) => {
      const t = line.trim();
      if (t === "[/DRAGDROP]") return;
      if (t === "--") { section = "options"; return; }
      if (section === "rows") {
        const parts = line.split("|");
        const n = parseInt(parts[0]?.trim());
        if (!isNaN(n)) items.set(n, { type: "matching", options: [] });
      }
    });
  });

  return [...items.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([num, meta]) => ({ num, ...meta }));
};

const IeltsManager = () => {
  const [subjects, setSubjects] = useState([]);
  const [tests, setTests] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [activeSection, setActiveSection] = useState("listening");
  const [subjectForm, setSubjectForm] = useState(emptySubject);
  const [testForm, setTestForm] = useState(emptyTest);
  const [editingSubjectId, setEditingSubjectId] = useState(null);
  const [editingTestId, setEditingTestId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [questionMode, setQuestionMode] = useState("template");

  const headers = useMemo(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const selectedSubject = subjects.find((subject) => String(subject.id) === String(selectedSubjectId));
  const visibleTests = tests.filter(
    (test) =>
      String(test.ielts_subject_id) === String(selectedSubjectId) &&
      test.section === activeSection
  );

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setTestForm((prev) => ({
      ...prev,
      ielts_subject_id: selectedSubjectId,
      section: activeSection,
    }));
  }, [selectedSubjectId, activeSection]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subjectResponse, testResponse] = await Promise.all([
        axios.get(`${apiUrl}/ielts-subjects`, { headers }),
        axios.get(`${apiUrl}/ielts-tests`, { headers }),
      ]);

      const subjectData = subjectResponse.data || [];
      setSubjects(subjectData);
      setTests(testResponse.data || []);

      if (!selectedSubjectId && subjectData.length) {
        setSelectedSubjectId(String(subjectData[0].id));
      }
    } catch (error) {
      setMessage(error.response?.data?.message || "Failed to load IELTS data.");
    } finally {
      setLoading(false);
    }
  };

  const resetSubjectForm = () => {
    setSubjectForm(emptySubject);
    setEditingSubjectId(null);
  };

  const resetTestForm = () => {
    setTestForm({
      ...emptyTest,
      ielts_subject_id: selectedSubjectId,
      section: activeSection,
    });
    setEditingTestId(null);
    setQuestionMode("template");
  };

  const saveSubject = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");

    try {
      if (editingSubjectId) {
        await axios.put(`${apiUrl}/ielts-subjects/${editingSubjectId}`, subjectForm, { headers });
        setMessage("IELTS subject updated.");
      } else {
        const response = await axios.post(`${apiUrl}/ielts-subjects`, subjectForm, { headers });
        setSelectedSubjectId(String(response.data.id));
        setMessage("IELTS subject created.");
      }

      resetSubjectForm();
      await fetchData();
    } catch (error) {
      setMessage(error.response?.data?.message || "Subject save failed.");
    } finally {
      setSaving(false);
    }
  };

  const saveTest = async (event) => {
    event.preventDefault();
    if (!testForm.ielts_subject_id) {
      setMessage("Create or select an IELTS subject first.");
      return;
    }

    setSaving(true);
    setMessage("");

    const formData = new FormData();
    Object.keys(testForm).forEach((key) => {
      if (key === "questions") {
        formData.append(key, JSON.stringify(testForm.questions));
      } else if ((key === "audio" || key === "image" || key === "image2" || key === "image3" || key === "logo") && testForm[key] instanceof File) {
        formData.append(key, testForm[key]);
      } else if (testForm[key] !== null && testForm[key] !== undefined) {
        if (typeof testForm[key] === "boolean") {
          formData.append(key, testForm[key] ? 1 : 0);
        } else {
          formData.append(key, testForm[key]);
        }
      }
    });

    try {
      const configWithMultipart = {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data",
        },
      };

      if (editingTestId) {
        await axios.post(`${apiUrl}/ielts-tests/${editingTestId}?_method=PUT`, formData, configWithMultipart);
        setMessage("IELTS practice updated.");
      } else {
        await axios.post(`${apiUrl}/ielts-tests`, formData, configWithMultipart);
        setMessage("IELTS practice created.");
      }

      resetTestForm();
      await fetchData();
    } catch (error) {
      setMessage(error.response?.data?.message || "Practice save failed.");
    } finally {
      setSaving(false);
    }
  };

  const editSubject = (subject) => {
    setEditingSubjectId(subject.id);
    setSubjectForm({
      name: subject.name || "",
      description: subject.description || "",
      order: subject.order || 0,
      is_active: Boolean(subject.is_active),
    });
  };

  const editTest = (test) => {
    setEditingTestId(test.id);
    setActiveSection(test.section);
    setSelectedSubjectId(String(test.ielts_subject_id));
    const hasTpl = /\[\[\d+\]\]|\[FLOW\]|\[BOX\]|\[GRID(?::|\])|\[TABLE(?::|\])/.test(test.question_text || "");
    setQuestionMode(hasTpl ? "template" : test.questions?.length > 0 ? "cards" : "template");
    setTestForm({
      ielts_subject_id: test.ielts_subject_id,
      section: test.section,
      title: test.title || "",
      passage_title: test.passage_title || "",
      passage_text: test.passage_text || "",
      instruction: test.instruction || "",
      question_title: test.question_title || "",
      question_text: test.question_text || "",
      questions: (test.questions?.length
        ? test.questions
        : (() => {
            const m = (test.question_text || "").match(/\[\[(\d+)\]\]/g) || [];
            return [...new Set(m.map((x) => parseInt(x.replace(/[[\]]/g, ""))))].sort((a, b) => a - b).map((n) => ({ number: n, type: "fill_blank", prompt: "", answer: "", after_text: "", options: [] }));
          })()
      ).map((q, i) => ({
        number: q.number || i + 1,
        prompt: q.prompt || "",
        type: ["text", "true_false", "mcq", "fill_blank", "flowchart", "table", "grid", "matching", "sentence_completion"].includes(q.type) ? q.type : "fill_blank",
        answer: q.answer || "",
        after_text: q.after_text || "",
        options: Array.isArray(q.options) ? q.options : [],
        explanation: q.explanation || "",
        metadata: q.metadata || {},
      })),
      audio_url: test.audio_url || "",
      image_url: test.image_url || "",
      image2_url: test.image2_url || "",
      image3_url: test.image3_url || "",
      logo_url: test.logo_url || "",
      sample_answer: test.sample_answer || "",
      time_limit: test.time_limit || 20,
      order: test.order || 0,
      is_published: Boolean(test.is_published),
    });
  };

  const deleteSubject = async (id) => {
    if (!window.confirm("Delete this IELTS subject and all practices?")) return;
    await axios.delete(`${apiUrl}/ielts-subjects/${id}`, { headers });
    setSelectedSubjectId("");
    await fetchData();
  };

  const deleteTest = async (id) => {
    if (!window.confirm("Delete this IELTS practice?")) return;
    await axios.delete(`${apiUrl}/ielts-tests/${id}`, { headers });
    await fetchData();
  };

  const updateTestField = (field, value) => {
    setTestForm((prev) => ({ ...prev, [field]: value }));
  };

  const loadExample = (partNumber) => {
    setQuestionMode("template");
    
    let title = "";
    let qTitle = "";
    let template = "";
    let qs = [];
    let passage = "";
    let passageTitle = "";
    
    if (activeSection === "reading") {
      if (partNumber === 1) {
          title = "Reading Part 1 Practice";
          qTitle = "Questions 1-13";
          template = readingPart1Template;
          qs = readingPart1Questions;
          passageTitle = "Fossil Files: 'The Paleobiology Database'";
          passage = `A\nAre we now living through the sixth extinction as our own activities destroy ecosystems and wipe out diversity? That's the doomsday scenario painted by many ecologists, and they may well be right. The trouble is, we don't know for sure because we don't have a clear picture of how life changes between extinction events or what has happened in previous episodes.\n\nB\nBut already the project is attracting harsh criticism. Some experts believe it to be seriously flawed. They point out that a database is only as good as the data fed into it, and that even if all the current fossil finds were catalogued, they would provide an incomplete inventory of life because we are far from discovering every fossilised species.\n\nC\nFans of the Paleobiology Database acknowledge that the fossil record will always be incomplete. But they see value in looking for global patterns that show relative changes in biodiversity.\n\nD\n"The fossil record is the best tool we have for understanding how diversity and extinction work in normal times," says John Alroy.`;
      } else if (partNumber === 2) {
          title = "Reading Part 2 Practice";
          qTitle = "Questions 14-26";
          template = readingPart2Template;
          qs = readingPart2Questions;
          passageTitle = "Voyage of Going: beyond the blue line 2";
          passage = `A\nOne feels a certain sympathy for Captain James Cook on the day in 1778 that he "discovered" Hawaii. Then on his third expedition to the Pacific, the British navigator had explored scores of islands across the breadth of the sea, from lush New Zealand to the lonely wastes of Easter Island. This latest voyage had taken him thousands of miles north from the Society Islands to an archipelago so remote that even the old Polynesians back on Tahiti knew nothing about it.\n\nB\nAnswers have been slow in coming. But now a startling archaeological find on the island of Éfaté, in the Pacific nation of Vanuatu, has revealed an ancient seafaring people, the distant ancestors of today's Polynesians, taking their first steps into the unknown.\n\nC\n"What we have is a first- or second-generation site containing the graves of some of the Pacific's first explorers," says Spriggs.`;
      } else if (partNumber === 3) {
          title = "Reading Part 3 Practice";
          qTitle = "Questions 27-40";
          template = readingPart3Template;
          qs = readingPart3Questions;
          passageTitle = "Biology of Bitterness";
          passage = `A\nThere is a reason why grapefruit juice is served in little glasses: most people don't want to drink more than a few ounces at a time. Naringin, a natural chemical compound found in grapefruit, tastes bitter. Some people like that bitterness in small doses and believe it enhances the general flavor, but others would rather avoid it altogether.\n\nB\nIt is possible, however, to get the goodness of grapefruit juice without the bitter taste. I found that out by participating in a test conducted at the Linguagen Corporation, a biotechnology company in Cranbury, New Jersey.\n\nC\nTaste research is a booming business these days, with scientists delving into all five basics—sweet, bitter, sour, salty, and umami, the savory taste of protein. Bitterness is of special interest to industry because of its untapped potential in food.`;
      }
    } else {
      if (partNumber === 1) {
          title = "Listening Part 1 Practice";
          qTitle = "Questions 1-10";
          template = listeningPart1Template;
          qs = listeningPart1Questions;
      } else if (partNumber === 2) {
          title = "Listening Part 2 Practice";
          qTitle = "Questions 11-20";
          template = listeningPart2Template;
          qs = listeningPart2Questions;
      } else if (partNumber === 3) {
          title = "Listening Part 3 Practice";
          qTitle = "Questions 21-30";
          template = listeningPart3Template;
          qs = listeningPart3Questions;
      } else if (partNumber === 4) {
          title = "Listening Part 4 Practice";
          qTitle = "Questions 31-40";
          template = listeningPart4Template;
          qs = listeningPart4Questions;
      }
    }

    setTestForm((prev) => ({
      ...prev,
      title: title,
      question_title: qTitle,
      instruction: "Answer all questions based on the instructions provided.",
      passage_title: passageTitle,
      passage_text: passage,
      question_text: template,
      questions: qs,
      time_limit: activeSection === "reading" ? 20 : 10,
    }));
    setMessage(`Part ${partNumber} example loaded. Review answers, then save the practice.`);
  };

  // ── Template-based blank detection ───────────────────────────────────────

  const detectedQuestions = useMemo(
    () => extractTemplateQuestions(testForm.question_text),
    [testForm.question_text]
  );

  const syncAnswersFromTemplate = useCallback((template) => {
    const items = extractTemplateQuestions(template);
    setTestForm((prev) => ({
      ...prev,
      questions: items.map(({ num, type, options }) => {
        const existing = prev.questions.find((q) => q.number === num);
        return existing
          ? { ...existing, type, options: options.length ? options : existing.options }
          : { number: num, type, prompt: "", answer: "", after_text: "", options, explanation: "", metadata: {} };
      }),
    }));
  }, []);

  const updateBlankAnswer = (num, value) => {
    setTestForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => (q.number === num ? { ...q, answer: value } : q)),
    }));
  };

  // ── Question builder helpers ──────────────────────────────────────────────

  const addQuestion = () => {
    setTestForm((prev) => ({
      ...prev,
      questions: [...prev.questions, newQuestion(prev.questions.length + 1)],
    }));
  };

  const removeQuestion = (index) => {
    setTestForm((prev) => ({
      ...prev,
      questions: prev.questions
        .filter((_, i) => i !== index)
        .map((q, i) => ({ ...q, number: i + 1 })),
    }));
  };

  const updateQuestion = (index, field, value) => {
    setTestForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === index ? { ...q, [field]: value } : q
      ),
    }));
  };

  const addOption = (qIndex) => {
    setTestForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === qIndex ? { ...q, options: [...q.options, ""] } : q
      ),
    }));
  };

  const removeOption = (qIndex, optIndex) => {
    setTestForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i !== qIndex) return q;
        const newOptions = q.options.filter((_, j) => j !== optIndex);
        return {
          ...q,
          options: newOptions,
          answer: q.answer === q.options[optIndex] ? "" : q.answer,
        };
      }),
    }));
  };

  const updateOption = (qIndex, optIndex, value) => {
    setTestForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i !== qIndex) return q;
        const newOptions = q.options.map((opt, j) => (j === optIndex ? value : opt));
        // Keep answer in sync if it matched the old value
        const answer = q.answer === q.options[optIndex] ? value : q.answer;
        return { ...q, options: newOptions, answer };
      }),
    }));
  };

  if (loading) {
    return <div className="p-6 text-gray-600">Loading IELTS manager...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">IELTS Content</h1>
          <p className="text-sm text-gray-600">Create IELTS subjects and add Listening, Reading, and Writing practice data.</p>
        </div>
        <a
          href="/ielts"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          Student View
        </a>
      </div>

      {message && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <section className="space-y-4 xl:col-span-1">
          <form onSubmit={saveSubject} className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-4 flex items-center text-lg font-semibold text-gray-900">
              <Plus className="mr-2 h-5 w-5 text-blue-600" />
              {editingSubjectId ? "Edit Subject" : "Create IELTS Subject"}
            </h2>
            <div className="space-y-3">
              <input
                value={subjectForm.name}
                onChange={(event) => setSubjectForm((prev) => ({ ...prev, name: event.target.value }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2"
                placeholder="IELTS / Academic IELTS"
                required
              />
              <textarea
                value={subjectForm.description}
                onChange={(event) => setSubjectForm((prev) => ({ ...prev, description: event.target.value }))}
                className="min-h-24 w-full rounded-md border border-gray-300 px-3 py-2"
                placeholder="Short description"
              />
              <input
                type="number"
                value={subjectForm.order}
                onChange={(event) => setSubjectForm((prev) => ({ ...prev, order: Number(event.target.value) }))}
                className="w-full rounded-md border border-gray-300 px-3 py-2"
                placeholder="Order"
              />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={subjectForm.is_active}
                  onChange={(event) => setSubjectForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                />
                Active
              </label>
              <div className="flex gap-2">
                <button disabled={saving} className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60">
                  <Save className="mr-2 h-4 w-4" />
                  Save
                </button>
                {editingSubjectId && (
                  <button type="button" onClick={resetSubjectForm} className="rounded-md border border-gray-300 px-4 py-2 text-gray-700">
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </form>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-3 text-lg font-semibold text-gray-900">Subjects</h2>
            <div className="space-y-2">
              {subjects.map((subject) => (
                <div
                  key={subject.id}
                  className={`rounded-md border p-3 ${String(subject.id) === String(selectedSubjectId) ? "border-blue-400 bg-blue-50" : "border-gray-200"}`}
                >
                  <button
                    type="button"
                    onClick={() => setSelectedSubjectId(String(subject.id))}
                    className="block w-full text-left font-semibold text-gray-900"
                  >
                    {subject.name}
                  </button>
                  <p className="mt-1 text-xs text-gray-500">{subject.description || "No description"}</p>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => editSubject(subject)} className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-700">
                      Edit
                    </button>
                    <button onClick={() => deleteSubject(subject.id)} className="rounded-md border border-red-200 px-3 py-1 text-xs text-red-600">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {!subjects.length && <p className="text-sm text-gray-500">No IELTS subject yet.</p>}
            </div>
          </div>
        </section>

        <section className="space-y-4 xl:col-span-2">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-4 flex flex-wrap gap-2">
              {sections.map((section) => {
                const Icon = sectionIcon[section];
                return (
                  <button
                    key={section}
                    type="button"
                    onClick={() => setActiveSection(section)}
                    className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold capitalize ${
                      activeSection === section ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    {section}
                  </button>
                );
              })}
            </div>

            <form onSubmit={saveTest} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Left Side: Media and Content */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold uppercase text-gray-400">Media & Content</h3>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">Subject</span>
                      <select
                        value={testForm.ielts_subject_id}
                        onChange={(event) => {
                          setSelectedSubjectId(event.target.value);
                          updateTestField("ielts_subject_id", event.target.value);
                        }}
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                        required
                      >
                        <option value="">Select subject</option>
                        {subjects.map((subject) => (
                          <option key={subject.id} value={subject.id}>{subject.name}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">Practice Title</span>
                      <input
                        value={testForm.title}
                        onChange={(event) => updateTestField("title", event.target.value)}
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                        placeholder="Transport Survey"
                        required
                      />
                    </label>
                  </div>

                  <div className="space-y-2">
                    <span className="block text-sm font-medium text-gray-700">Audio (Listening)</span>
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={(event) => updateTestField("audio", event.target.files[0])}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                    />
                    <input
                      value={testForm.audio_url}
                      onChange={(event) => updateTestField("audio_url", event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      placeholder="Or paste Audio URL"
                    />
                  </div>

                  <div className="space-y-2">
                    <span className="block text-sm font-medium text-gray-700">Image 1 (Writing/Reading)</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => updateTestField("image", event.target.files[0])}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                    />
                    <input
                      value={testForm.image_url}
                      onChange={(event) => updateTestField("image_url", event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      placeholder="Or paste Image URL"
                    />
                  </div>

                  <div className="space-y-2">
                    <span className="block text-sm font-medium text-gray-700">Image 2 (Optional)</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => updateTestField("image2", event.target.files[0])}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                    />
                    <input
                      value={testForm.image2_url}
                      onChange={(event) => updateTestField("image2_url", event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      placeholder="Or paste Image 2 URL"
                    />
                  </div>

                  <div className="space-y-2">
                    <span className="block text-sm font-medium text-gray-700">Image 3 (Optional)</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => updateTestField("image3", event.target.files[0])}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                    />
                    <input
                      value={testForm.image3_url}
                      onChange={(event) => updateTestField("image3_url", event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      placeholder="Or paste Image 3 URL"
                    />
                  </div>

                  <div className="space-y-2">
                    <span className="block text-sm font-medium text-gray-700">Logo (Student View)</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => updateTestField("logo", event.target.files[0])}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                    />
                    <input
                      value={testForm.logo_url}
                      onChange={(event) => updateTestField("logo_url", event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      placeholder="Or paste Logo URL"
                    />
                  </div>

                  <div className="space-y-2">
                    <span className="block text-sm font-medium text-gray-700">Passage Title</span>
                    <input
                      value={testForm.passage_title}
                      onChange={(event) => updateTestField("passage_title", event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2"
                      placeholder="Passage / Task title"
                    />
                  </div>

                  <div className="space-y-2">
                    <span className="block text-sm font-medium text-gray-700">Passage Text / Transcript</span>
                    <textarea
                      value={testForm.passage_text}
                      onChange={(event) => updateTestField("passage_text", event.target.value)}
                      className="min-h-[300px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      placeholder="Left-side passage/transcript. Use [[highlighted text|1]] to highlight answer evidence."
                    />
                  </div>
                </div>

                {/* Right Side: Questions and Settings */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold uppercase text-gray-400">Questions & Settings</h3>

                  <div className="grid grid-cols-2 gap-4">
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">Time Limit (min)</span>
                      <input
                        type="number"
                        value={testForm.time_limit}
                        onChange={(event) => updateTestField("time_limit", Number(event.target.value))}
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">Order</span>
                      <input
                        type="number"
                        value={testForm.order}
                        onChange={(event) => updateTestField("order", Number(event.target.value))}
                        className="w-full rounded-md border border-gray-300 px-3 py-2"
                      />
                    </label>
                  </div>

                  <div className="space-y-2">
                    <span className="block text-sm font-medium text-gray-700">Question Title</span>
                    <input
                      value={testForm.question_title}
                      onChange={(event) => updateTestField("question_title", event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2"
                      placeholder="Question title, e.g. Transport survey"
                    />
                  </div>

                  <div className="space-y-2">
                    <span className="block text-sm font-medium text-gray-700">Instructions</span>
                    <textarea
                      value={testForm.instruction}
                      onChange={(event) => updateTestField("instruction", event.target.value)}
                      className="min-h-[80px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      placeholder="Instructions shown above questions"
                    />
                  </div>

                  {/* ── Mode toggle ── */}
                  <div className="space-y-3">
                    <div className="flex rounded-lg overflow-hidden border border-gray-200">
                      <button type="button"
                        onClick={() => setQuestionMode("template")}
                        className={`flex-1 px-3 py-2 text-xs font-bold transition-colors ${
                          questionMode === "template" ? "bg-gray-900 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        📝 Template (Fill-blank / Flow / Grid / Table)
                      </button>
                      <button type="button"
                        onClick={() => setQuestionMode("cards")}
                        className={`flex-1 px-3 py-2 text-xs font-bold border-l border-gray-200 transition-colors ${
                          questionMode === "cards" ? "bg-gray-900 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        🃏 MCQ Card Builder
                      </button>
                    </div>

                    {/* ── Template mode ── */}
                    {questionMode === "template" && (
                      <>
                        <div className="space-y-2">
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <span className="text-sm font-medium text-gray-700">Question Template</span>
                              <span className="rounded bg-blue-50 border border-blue-200 px-2 py-0.5 text-xs text-blue-700 font-mono">
                                [[N]] · [BOX] · [GRID:] · [TABLE:] · [DRAGDROP]
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <button type="button" onClick={() => loadExample(1)} className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 transition-colors hover:bg-indigo-100">
                                Part 1 Example
                              </button>
                              <button type="button" onClick={() => loadExample(2)} className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 transition-colors hover:bg-indigo-100">
                                Part 2 Example
                              </button>
                              <button type="button" onClick={() => loadExample(3)} className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 transition-colors hover:bg-indigo-100">
                                Part 3 Example
                              </button>
                              {activeSection !== "reading" && (
                                <button type="button" onClick={() => loadExample(4)} className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 transition-colors hover:bg-indigo-100">
                                  Part 4 Example
                                </button>
                              )}
                            </div>
                          </div>
                          <textarea
                            value={testForm.question_text}
                            onChange={(event) => {
                              updateTestField("question_text", event.target.value);
                              syncAnswersFromTemplate(event.target.value);
                            }}
                            className="min-h-[220px] w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm leading-6"
                            placeholder={"── Fill Blank ──\nContact number: [[1]]\nPrice: $[[2]] daily\n\n── Flow Chart (boxes auto-close on ↓) ──\n[BOX]\nStudent leaves home at [[6]]\n↓\n[BOX]\nTakes the [[7]] bus\n↓\n[BOX]\nArrives at [[8]]\n\n── Grid MCQ ──\n[GRID:A,B,C,D]\n17|First Crack\n18|Green Beans\n[/GRID]\n\n── Table with Blanks ──\n[TABLE:Time Keeper|Disadvantages]\nSundial|only works [[36]]\n[/TABLE]"}
                          />
                          <p className="text-xs text-gray-400">
                            <strong className="font-mono text-gray-600">[[N]]</strong> = blank &nbsp;·&nbsp;
                            <strong className="font-mono text-gray-600">[BOX]</strong> = box (auto-closes on next ↓ or [BOX]) &nbsp;·&nbsp;
                            <strong className="font-mono text-gray-600">↓</strong> / <strong className="font-mono text-gray-600">→</strong> = arrow &nbsp;·&nbsp;
                            <strong className="font-mono text-gray-600">[GRID:A,B,C,D]</strong> = MCQ grid &nbsp;·&nbsp;
                            <strong className="font-mono text-gray-600">[TABLE:H1|H2]</strong> = table
                          </p>
                        </div>

                        {detectedQuestions.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between flex-wrap gap-1">
                              <span className="text-sm font-medium text-gray-700">
                                Answer Key ({detectedQuestions.length} detected)
                              </span>
                              <span className="text-xs text-gray-400">[[N]] = blank · GRID rows = MCQ</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {detectedQuestions.map(({ num, type, options }) => {
                                const q = testForm.questions.find((q) => q.number === num) || {};
                                return (
                                  <div key={num} className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                                    <span className={`w-8 shrink-0 rounded px-1.5 py-0.5 text-center text-xs font-bold ${
                                      type === "mcq" ? "bg-purple-100 text-purple-700" : "bg-indigo-100 text-indigo-700"
                                    }`}>
                                      [{num}]
                                    </span>
                                    {type === "mcq" && options.length ? (
                                      <select
                                        value={q.answer || ""}
                                        onChange={(e) => updateBlankAnswer(num, e.target.value)}
                                        className="flex-1 bg-transparent text-sm outline-none border-b border-gray-300 focus:border-blue-500 py-0.5"
                                      >
                                        <option value="">Select answer</option>
                                        {options.map((opt) => (
                                          <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                      </select>
                                    ) : (
                                      <input
                                        value={q.answer || ""}
                                        onChange={(e) => updateBlankAnswer(num, e.target.value)}
                                        className="flex-1 bg-transparent text-sm outline-none border-b border-gray-300 focus:border-blue-500 py-0.5"
                                        placeholder={`Answer for ${num}`}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* ── MCQ Card Builder mode ── */}
                    {questionMode === "cards" && (
                      <div className="space-y-3">
                        <p className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                          Add mixed questions below. MCQ, matching, blanks, table/grid rows, and sentence completion can share the same practice.
                        </p>

                        {testForm.questions.map((q, idx) => {
                          const LETTERS = ["A", "B", "C", "D", "E", "F", "G"];
                          const isMcq = q.type === "mcq" || q.type === "matching";
                          const isTF = q.type === "true_false";
                          const isText = q.type === "text" || q.type === "fill_blank" || q.type === "sentence_completion" || q.type === "flowchart" || q.type === "table" || q.type === "grid";

                          return (
                            <div key={idx} className="rounded-xl border border-gray-200 bg-white p-4 space-y-3 shadow-sm">
                              {/* Header: number + type + delete */}
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-bold text-gray-500 uppercase">Q</span>
                                <input
                                  type="number"
                                  value={q.number}
                                  onChange={(e) => updateQuestion(idx, "number", parseInt(e.target.value) || 1)}
                                  className="w-16 rounded-md border border-gray-300 px-2 py-1 text-sm text-center font-bold focus:border-blue-500 focus:outline-none"
                                  min="1"
                                />
                                <select
                                  value={q.type}
                                  onChange={(e) => updateQuestion(idx, "type", e.target.value)}
                                  className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                                >
                                  <option value="mcq">Multiple Choice (MCQ)</option>
                                  <option value="true_false">True / False / Not Given</option>
                                  <option value="fill_blank">Fill in the Blank</option>
                                  <option value="sentence_completion">Sentence Completion</option>
                                  <option value="matching">Matching</option>
                                  <option value="flowchart">Flowchart</option>
                                  <option value="table">Table</option>
                                  <option value="grid">Grid</option>
                                </select>
                                <button
                                  type="button"
                                  onClick={() => removeQuestion(idx)}
                                  className="ml-auto rounded-md border border-red-200 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>

                              {/* Question prompt */}
                              <textarea
                                value={q.prompt}
                                onChange={(e) => updateQuestion(idx, "prompt", e.target.value)}
                                placeholder={
                                  isMcq ? "Question text, e.g. What does the speaker say about...?"
                                  : isTF ? "Statement for student to evaluate..."
                                  : "Text before the blank (e.g. The manager is named)"
                                }
                                rows={2}
                                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-none focus:border-blue-500 focus:outline-none"
                              />

                              {/* MCQ options */}
                              {isMcq && (
                                <div className="space-y-2 pl-1">
                                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                                    Options — click ○ to mark correct answer
                                  </p>
                                  {q.options.map((opt, oi) => (
                                    <div key={oi} className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => updateQuestion(idx, "answer", opt)}
                                        title="Mark as correct answer"
                                        className={`flex-shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                          q.answer === opt
                                            ? "border-green-500 bg-green-500"
                                            : "border-gray-300 hover:border-green-400"
                                        }`}
                                      >
                                        {q.answer === opt && (
                                          <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                          </svg>
                                        )}
                                      </button>
                                      <span className="flex-shrink-0 w-5 text-xs font-bold text-gray-400">
                                        {LETTERS[oi]}
                                      </span>
                                      <input
                                        value={opt}
                                        onChange={(e) => updateOption(idx, oi, e.target.value)}
                                        placeholder={`Option ${LETTERS[oi]}`}
                                        className="flex-1 rounded-md border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => removeOption(idx, oi)}
                                        className="flex-shrink-0 text-gray-300 hover:text-red-500 transition-colors"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() => addOption(idx)}
                                    className="text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors py-1"
                                  >
                                    + Add Option
                                  </button>
                                  {q.answer && (
                                    <p className="text-xs font-semibold text-green-700 flex items-center gap-1">
                                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                      </svg>
                                      Correct: {q.answer}
                                    </p>
                                  )}
                                </div>
                              )}

                              {/* True / False answer */}
                              {isTF && (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs font-semibold text-gray-600">Correct answer:</span>
                                  {["TRUE", "FALSE", "NOT GIVEN"].map((opt) => (
                                    <button
                                      key={opt}
                                      type="button"
                                      onClick={() => updateQuestion(idx, "answer", opt)}
                                      className={`px-3 py-1 rounded-full text-xs font-bold border-2 transition-all ${
                                        q.answer === opt
                                          ? "border-green-500 bg-green-500 text-white"
                                          : "border-gray-200 text-gray-600 hover:border-green-300 hover:text-green-700"
                                      }`}
                                    >
                                      {opt}
                                    </button>
                                  ))}
                                </div>
                              )}

                              {/* Fill blank: answer + after text */}
                              {isText && (
                                <div className="space-y-2">
                                  <input
                                    value={q.answer}
                                    onChange={(e) => updateQuestion(idx, "answer", e.target.value)}
                                    placeholder="Correct answer for this blank"
                                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                                  />
                                  <input
                                    value={q.after_text}
                                    onChange={(e) => updateQuestion(idx, "after_text", e.target.value)}
                                    placeholder="Text after the blank (optional)"
                                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                                  />
                                </div>
                              )}

                              <textarea
                                value={q.explanation || ""}
                                onChange={(e) => updateQuestion(idx, "explanation", e.target.value)}
                                placeholder="Explanation shown after submission (optional)"
                                rows={2}
                                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-none focus:border-blue-500 focus:outline-none"
                              />
                            </div>
                          );
                        })}

                        <button
                          type="button"
                          onClick={addQuestion}
                          className="w-full rounded-xl border-2 border-dashed border-indigo-300 py-3 text-sm font-bold text-indigo-600 hover:border-indigo-500 hover:bg-indigo-50 transition-all"
                        >
                          + Add Question
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <span className="block text-sm font-medium text-gray-700">Sample Answer</span>
                    <textarea
                      value={testForm.sample_answer}
                      onChange={(event) => updateTestField("sample_answer", event.target.value)}
                      className="min-h-[120px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      placeholder="Sample answer or model response"
                    />
                  </div>

                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={testForm.is_published}
                      onChange={(event) => updateTestField("is_published", event.target.checked)}
                    />
                    Published for students
                  </label>

                  <div className="flex gap-2 pt-4">
                    <button disabled={saving} className="inline-flex flex-1 items-center justify-center rounded-md bg-green-600 px-4 py-3 font-bold text-white transition-colors hover:bg-green-700 disabled:opacity-60">
                      <Save className="mr-2 h-5 w-5" />
                      {editingTestId ? "Update Practice" : "Create Practice"}
                    </button>
                    {editingTestId && (
                      <button type="button" onClick={resetTestForm} className="rounded-md border border-gray-300 px-6 py-3 text-gray-700 hover:bg-gray-50">
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-1 text-lg font-semibold capitalize text-gray-900">
              {activeSection} practices {selectedSubject ? `for ${selectedSubject.name}` : ""}
            </h2>
            <p className="mb-4 text-sm text-gray-500">Students only see published items.</p>
            <div className="space-y-3">
              {visibleTests.map((test) => (
                <div key={test.id} className="flex flex-col gap-3 rounded-md border border-gray-200 p-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{test.title}</h3>
                    <p className="text-sm text-gray-500">
                      {test.questions?.length || 0} questions | {test.time_limit} min | {test.is_published ? "Published" : "Draft"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => editTest(test)} className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700">
                      <Edit className="mr-1 h-4 w-4" />
                      Edit
                    </button>
                    <button onClick={() => deleteTest(test.id)} className="inline-flex items-center rounded-md border border-red-200 px-3 py-2 text-sm text-red-600">
                      <Trash2 className="mr-1 h-4 w-4" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {!visibleTests.length && (
                <p className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                  No {activeSection} practice added yet.
                </p>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default IeltsManager;
